import { CustomFrame, PhotoPosition } from '../types';
import { getSessionToken } from './sessionToken';
import { API_URL } from '../config/api';

const API_BASE_URL = API_URL.replace(/\/api\/?$/i, '');

export interface CreateCustomFrameData {
  name: string;
  size: '2x4' | '4x2' | '2x6' | '6x2' | '4x6' | '6x4' | '3x4' | '4x3';
  photoPositions: PhotoPosition[];
  link?: string;
}

export interface UpdateCustomFrameData {
  name?: string;
  size?: '2x4' | '4x2' | '2x6' | '6x2' | '4x6' | '6x4' | '3x4' | '4x3';
  photoPositions?: PhotoPosition[];
  isActive?: boolean;
  link?: string;
}

export interface CustomFrameResponse {
  message: string;
  frame: CustomFrame;
}

export interface CustomFramesResponse {
  frames: CustomFrame[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class CustomFrameService {
  private getAuthHeaders(): HeadersInit {
    const token = getSessionToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  // Lấy danh sách tất cả custom frames
  async getCustomFrames(params?: {
    size?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<CustomFramesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.size) queryParams.append('size', params.size);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await fetch(`${API_BASE_URL}/api/frames?${queryParams}`, {
      method: 'GET',
      credentials: 'include', headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch custom frames');
    }

    return response.json();
  }

  /**
   * Custom frame đã bật cho user trên M-Photo Pro (desktop) — cùng nguồn với `/custom-frames-pro` và app WPF.
   * Không dùng `/custom-frames` (đó là Lite / mobile upload HTML + CustomFrameEnableLite).
   */
  async getEnabledCustomFramesForUser(params: {
    userId: string;
    size?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; frames: CustomFrame[]; pagination?: CustomFramesResponse['pagination'] }> {
    const queryParams = new URLSearchParams();
    queryParams.append('userId', params.userId);
    if (params.size) queryParams.append('size', params.size);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await fetch(`${API_BASE_URL}/api/mobile-upload/custom-frames-pro?${queryParams}`, {
      method: 'GET',
      credentials: 'include', headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Failed to fetch enabled custom frames');
    }

    return response.json();
  }

  // Lấy custom frame theo ID
  async getCustomFrameById(frameId: string): Promise<{ frame: CustomFrame }> {
    const response = await fetch(`${API_BASE_URL}/api/frames/${frameId}`, {
      method: 'GET',
      credentials: 'include', headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch custom frame');
    }

    return response.json();
  }

  // Tạo custom frame mới
  async createCustomFrame(data: CreateCustomFrameData): Promise<CustomFrameResponse> {
    const response = await fetch(`${API_BASE_URL}/api/frames`, {
      method: 'POST',
      credentials: 'include', headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create custom frame');
    }

    return response.json();
  }

  // Cập nhật custom frame
  async updateCustomFrame(frameId: string, data: UpdateCustomFrameData): Promise<CustomFrameResponse> {
    const response = await fetch(`${API_BASE_URL}/api/frames/${frameId}`, {
      method: 'PUT',
      credentials: 'include', headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update custom frame');
    }

    return response.json();
  }

  // Xóa custom frame
  async deleteCustomFrame(frameId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/frames/${frameId}`, {
      method: 'DELETE',
      credentials: 'include', headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete custom frame');
    }

    return response.json();
  }

  // Lấy custom frames theo size
  async getCustomFramesBySize(size: string, isActive: boolean = true): Promise<{ frames: CustomFrame[] }> {
    const response = await fetch(`${API_BASE_URL}/api/frames/size/${size}?isActive=${isActive}`, {
      method: 'GET',
      credentials: 'include', headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch custom frames by size');
    }

    return response.json();
  }
}

export default new CustomFrameService();
